import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, ZoomOut } from 'lucide-react';

/**
 * Full-screen media viewer.
 * - Images: object-contain (full aspect ratio preserved, no stretching).
 * - Videos: native controls, autoplay, full aspect ratio.
 * - Esc / backdrop / close button to dismiss; prev/next for galleries.
 * - Images zoom inside the viewer only: pinch, double-tap / double-click,
 *   mouse wheel; drag to pan when zoomed; reset button. The page itself is
 *   never zoomed, so the app's layout and navigation are untouched.
 */

const MAX_ZOOM = 5;

function ZoomableImage({ src }) {
  const [view, setView] = useState({ k: 1, x: 0, y: 0 });
  const ref = useRef(null);
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const lastTap = useRef(0);

  useEffect(() => { setView({ k: 1, x: 0, y: 0 }); }, [src]);

  // Keep the image covering its own box when zoomed (no panning into the void).
  const clamp = useCallback((v) => {
    const el = ref.current;
    if (!el || v.k <= 1) return { k: 1, x: 0, y: 0 };
    const mx = (el.clientWidth * (v.k - 1)) / 2;
    const my = (el.clientHeight * (v.k - 1)) / 2;
    return { k: v.k, x: Math.max(-mx, Math.min(mx, v.x)), y: Math.max(-my, Math.min(my, v.y)) };
  }, []);

  // Zoom keeping the point under (cx, cy) in place.
  const zoomAt = useCallback((k, cx, cy, from) => {
    const el = ref.current;
    if (!el) return from;
    const r = el.getBoundingClientRect();
    const px = cx - (r.left + r.width / 2);
    const py = cy - (r.top + r.height / 2);
    const nk = Math.max(1, Math.min(MAX_ZOOM, k));
    const f = nk / from.k;
    return clamp({ k: nk, x: px - (px - from.x) * f, y: py - (py - from.y) * f });
  }, [clamp]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      setView((v) => zoomAt(v.k * (e.deltaY < 0 ? 1.15 : 1 / 1.15), e.clientX, e.clientY, v));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  const down = (e) => {
    e.stopPropagation();
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture?.(e.pointerId);
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current = { type: 'pinch', d: Math.hypot(a.x - b.x, a.y - b.y), start: view, cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 };
    } else {
      const now = Date.now();
      if (now - lastTap.current < 300) {               // double tap / double click
        setView((v) => (v.k > 1 ? { k: 1, x: 0, y: 0 } : zoomAt(2.5, e.clientX, e.clientY, v)));
        lastTap.current = 0;
        gesture.current = null;
        return;
      }
      lastTap.current = now;
      gesture.current = { type: 'pan', sx: e.clientX, sy: e.clientY, start: view };
    }
  };
  const move = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;
    if (g.type === 'pinch' && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      setView(zoomAt(g.start.k * (Math.hypot(a.x - b.x, a.y - b.y) / g.d), g.cx, g.cy, g.start));
    } else if (g.type === 'pan' && g.start.k > 1) {
      setView(clamp({ ...g.start, x: g.start.x + e.clientX - g.sx, y: g.start.y + e.clientY - g.sy }));
    }
  };
  const up = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2 && gesture.current?.type === 'pinch') gesture.current = null;
    if (pointers.current.size === 0) gesture.current = null;
  };

  const zoomed = view.k > 1.01;
  return (
    <>
      <img
        ref={ref}
        src={src}
        alt=""
        draggable={false}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[92vw] max-h-[88vh] w-auto h-auto object-contain rounded-lg select-none"
        style={{
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})`,
          transition: gesture.current ? 'none' : 'transform 160ms ease-out',
          touchAction: 'none',
          cursor: zoomed ? 'grab' : 'zoom-in',
        }}
        data-testid="lightbox-image"
        data-zoom={view.k.toFixed(2)}
      />
      {zoomed && (
        <button
          onClick={(e) => { e.stopPropagation(); setView({ k: 1, x: 0, y: 0 }); }}
          className="fixed bottom-5 left-1/2 -translate-x-1/2 h-10 px-4 rounded-full bg-white/15 hover:bg-white/25 text-white text-sm flex items-center gap-2 z-10"
          data-testid="lightbox-reset-zoom"
        >
          <ZoomOut className="w-4 h-4" /> Reset zoom
        </button>
      )}
    </>
  );
}
export default function MediaLightbox({ open, items = [], startIndex = 0, onClose }) {
  const [index, setIndex] = useState(startIndex);

  useEffect(() => { if (open) setIndex(startIndex); }, [open, startIndex]);

  const go = useCallback((dir) => {
    setIndex((i) => (i + dir + items.length) % items.length);
  }, [items.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowRight' && items.length > 1) go(1);
      else if (e.key === 'ArrowLeft' && items.length > 1) go(-1);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, items.length, go, onClose]);

  if (!open || items.length === 0) return null;
  const current = items[index];

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/92 backdrop-blur-sm flex items-center justify-center animate-fadeIn"
      onClick={onClose}
      data-testid="media-lightbox"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-10"
        data-testid="lightbox-close"
        aria-label="Close"
      >
        <X className="w-6 h-6" />
      </button>

      {items.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            className="absolute left-3 md:left-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-10"
            data-testid="lightbox-prev"
            aria-label="Previous"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); go(1); }}
            className="absolute right-3 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-10"
            data-testid="lightbox-next"
            aria-label="Next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <span className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/70 text-sm">
            {index + 1} / {items.length}
          </span>
        </>
      )}

      <div className="max-w-[92vw] max-h-[88vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
        {current.type === 'video' ? (
          <video
            key={current.url}
            src={current.url}
            className="max-w-[92vw] max-h-[88vh] w-auto h-auto object-contain rounded-lg"
            controls
            autoPlay
            playsInline
            data-testid="lightbox-video"
          />
        ) : (
          <ZoomableImage src={current.url} />
        )}
      </div>
    </div>,
    document.body
  );
}
